import { removeSession, serveRedirect } from 'src/lib/response'
import debug from 'debug'
import pathToRegexp from 'path-to-regexp'
import { NextPageContext } from 'next'
import * as http from 'http'
import { getPath, getRedirectUrl } from 'src/lib/auth-helper'

const log = debug('yd:middleware')

const getHost = (req?: http.IncomingMessage) => {
  return req ? req.headers.host : window.location.hostname
}

// these are pages which users can view without
// a networkName in the url subdomain
const globalPages = [
  '/',
  '/_info',

  '/lti/launch',
  '/lti/userlink',
  '/lti/verification',

  '/error/safari',
  '/error/safari-confirm',

  '/insights/access-denied',

  '/share/(.*)',

  '/sso/contract-expired',

  '/test/*',
  '/test/conversations',

  '/user/create',
  '/user/emails',
  '/user/find-organization',
  '/user/login',
  '/user/not-authorized',
  '/user/select-organization',
  '/user/recoverpassword',
  '/user/resetpassword/(.*)',
  '/user/sso',
  '/user/verification',
  '/user/verification-success',
  '/user/verify-email'
]

// Get the network name from the subdomain of the request url
export const getNetworkSubdomain = ({
  req,
  res
}: {
  req?: http.IncomingMessage
  res?: http.OutgoingMessage
}) => {
  const path = getPath(req)
  if (!path) return null

  const host = getHost(req)
  if (!host) return null

  const parts = host.split('.')

  // handle case when user is trying to access page
  // without netowrk in subdomain of url
  const noNetworkSubdomain = parts.length < 3
  const pageIsGlobal = globalPages.some(p => pathToRegexp(p).exec(path))

  // Temporary we put all new pages to the /n/ folder
  const newNetworkURL = path.startsWith('/n/')

  if (noNetworkSubdomain && !pageIsGlobal && !newNetworkURL) {
    serveRedirect(res, '', '/')
    return null
  }

  if (newNetworkURL) {
    return path.replace('/n/', '').split('/')[0]
  }

  // no network name
  if (parts.length < 3) {
    return null
  }

  if (parts.length >= 3) {
    return parts[0]
  }
}

const handleErrorRedirects = (
  { req, res, pathname, asPath = '', query }: NextPageContext,
  errorData: {
    error: string
    ':yd-anom/message': string
    redirectBoardURL?: string
  }
) => {
  const {
    error,
    ':yd-anom/message': message,
    redirectBoardURL
  } = errorData || {}

  const redirectPath = req ? getPath(req) : '/'
  const redirect = encodeURIComponent(redirectPath ?? '/')

  if (error === 'expired-session') {
    removeSession(res, '/user/login')
    return true
  }

  if (error === 'invalid-session') {
    removeSession(res, '/user/login')
    return true
  }

  if (error === 'not-authorized' || message === 'Invalid CSRF Token') {
    const redirectUrl = getRedirectUrl('/user/not-authorized', req, redirect)
    removeSession(res, redirectUrl)
    return true
  }

  if (error === 'pending-network-invitation') {
    const isNewNetwork = pathname.startsWith('/n/[network]')
    const [networkName] = asPath.replace('/n/', '').split('/')
    const network = networkName || query.network
    const redirectUrlPrefix = isNewNetwork && network ? `/n/${network}` : ''

    const redirectUrl = getRedirectUrl(
      `${redirectUrlPrefix}/organization/invitation`,
      req,
      redirect
    )

    serveRedirect(res, null, redirectUrl)
    return true
  }

  if (error === 'user-not-verified') {
    serveRedirect(res, null, '/user/verification')
    return true
  }

  if (error === 'must-request-to-join' && redirectBoardURL) {
    serveRedirect(null, '', redirectBoardURL)
  }
}

export const handleErrorData = (
  ctx: NextPageContext,
  errorData: { error: string; ':yd-anom/message': string }
) => {
  log('handle error', errorData)

  const { error } = errorData

  if (error === 'request-timeout') {
    return errorData
  }

  if (error === 'query-timeout') {
    return errorData
  }

  if (error === 'bad-query') {
    return errorData
  }

  if (error === 'not-a-network') {
    return errorData
  }

  if (error === 'invalid-network') {
    return errorData
  }

  if (error === 'internal-server-error') {
    return errorData
  }

  if (error === 'invalid-share-link-token') {
    return errorData
  }

  if (error === 'network-contract-expired') {
    return errorData
  }

  if (handleErrorRedirects(ctx, errorData)) {
    return error
  }
  throw new Error(JSON.stringify(errorData))
}

export const handleApolloError = (e: $TSFixMe, ctx: NextPageContext) => {
  if (e.clientErrors.length !== 0) {
    return { error: 'gql-client-error' }
  }

  if (e.graphQLErrors.length !== 0) {
    const error = e.graphQLErrors[0]
    const anom = error.extensions?.anom
    if (!anom) {
      return { error: 'gql-error' }
    }
    const anomCategory = anom['yd-anom/category']
    const anomErrorCode = anom['yd-anom/error-code']

    if (anomCategory === 'forbidden') {
      if (anomErrorCode === 'board-is-archived') {
        return { error: 'board-is-archived' }
      } else if (anomErrorCode === 'contract-expired') {
        const anomData = error.extensions.anom['yd-anom/data'] || {}
        return {
          error: 'contract-expired',
          data: {
            // Backends deployed before the role field send is-network-admin
            // instead; map it so the right copy still renders.
            role:
              anomData.role ??
              (anomData['is-network-admin'] ? 'network-admin' : 'member')
          }
        }
      } else if (anomErrorCode === 'user-blocked-from-board') {
        return { error: 'user-blocked-from-board' }
      } else if (anomErrorCode === 'must-request-to-join') {
        const data = error.extensions.anom['yd-anom/data']
        const { res } = ctx
        return serveRedirect(
          res,
          '',
          `/n/${data['network-name']}/community/${data.board}/join`
        )
      } else if (anomErrorCode === 'pending-board-invitation') {
        const data = error.extensions.anom['yd-anom/data']
        const { res } = ctx
        return serveRedirect(
          res,
          '',
          `/n/${data['network-name']}/community/${data.board}/invitation`
        )
      }
      return { error: 'not-allowed' }
    }

    if (anomCategory === 'not-found') {
      return { error: 'not-found' }
    }
    if (anomCategory === 'busy') {
      return { error: anomErrorCode }
    }
    return { error: 'gql-error' }
  }
  if (e.networkError.length !== 0) {
    const { networkError } = e
    const { result } = networkError
    const { error } = result
    if (error === 'request-timeout') {
      return result
    }

    if (error === 'query-timeout') {
      return result
    }

    if (error === 'bad-query') {
      return result
    }

    if (error === 'not-a-network') {
      return result
    }

    if (error === 'invalid-network') {
      return result
    }

    if (error === 'internal-server-error') {
      return result
    }

    // backend is returning 'user not found' string here, this code here is similar to what we have in server-graphql-request.tsx
    if (result === 'user not found') {
      return handleErrorRedirects(ctx, {
        error: 'not-authorized',
        ':yd-anom/message': 'user not found'
      })
    }

    if (handleErrorRedirects(ctx, result)) {
      return error
    }

    throw new Error(JSON.stringify(result))
  }
}
